import PublicPage from '@/shared/PublicPage/PublicPage';
import { StyledAbout } from './About.styled';
import FlipOnScrollImage from '@/shared/FlipOnScrollImage/FlipOnScrollImage';

export default function About() {
  return (
    <PublicPage withFooter>
      <StyledAbout>
        <p className="intro">
          “Never doubt that a small group of thoughtful, committed citizens can
          change the world. Indeed, it is the only thing that ever has.”
          <br />
          <span>- Margaret Mead</span>
        </p>
        <p>
          We never set out to change the world… but hey, why not change the way
          Spanish is taught?
        </p>
        <p>
          We might not have superpowers, but we do have music, illustrations,
          and games&mdash;and that's more than enough to shake up how Spanish is
          taught and learned, one catchy tune at a time!
        </p>
        <p>
          No investors, no business experience, and definitely no business plan
          (we still don't have one!). And yet, here we are&mdash;trusted by
          thousands of teachers and families around the world.
        </p>
        <p>
          Meet the team behind Rockalingua&mdash;a crew of passionate
          professionals from around the world who pour their time, creativity,
          and love into making the best audiovisual learning materials and the
          tech to bring them to teachers and students everywhere.
        </p>
        <div className="people">
          {people.map((person, index) => {
            return (
              <div className="person" key={index}>
                <FlipOnScrollImage
                  className="person-image"
                  frontImage={person.image}
                  backImage={person.realImage}
                />
                <div className="person-info">
                  <h3 className="person-name">{person.name}</h3>
                  <p className="person-role">{person.role}</p>
                </div>
                <div className="person-description">{person.description}</div>
              </div>
            );
          })}
        </div>
      </StyledAbout>
    </PublicPage>
  );
}

const people = [
  {
    name: 'César',
    role: 'Founder & Song writer',
    description: (
      <>
        <p>
          Meet Cesar Alvarez! Hailing from Madrid, Cesar made his way to San
          Francisco in 2007 with plans for a brief adventure teaching Spanish.
          Seventeen years, a wife, and two kids later, he's still here—and
          loving every minute of it!
        </p>
        <p>
          Rockalingua was born in the classroom where César used his guitar to
          teach Spanish through music. He saw that teaching in a context kids
          can relate to and actually like, really worked!
        </p>
        <p>
          When he isn't crafting catchy tunes for Rockalingua, you'll likely
          find him savoring a cup of coffee while dreaming up his next musical
          masterpiece. Yep, music and coffee are his two ultimate obsessions!
        </p>
      </>
    ),
    image: 'img/about/1_CESAR.png',
    realImage: 'img/about/real/1_CesarAlvarez.png',
  },
  {
    name: 'Dani',
    role: 'Creative Director',
    description: (
      <>
        <p>
          Dani developed his artistic career in Madrid, Spain. For more than 20
          years he has drawn, painted, photographed (and even tattooed) for and
          with his friends.
        </p>
        <p>
          In 2012 he created Rockalingua with César, one of his friends from
          high school (remember he only works for and with friends), a project
          that he loves madly.{' '}
        </p>
        <p>
          From Madrid, with the help of some little elves, he illustrates,
          animates, and gives splendor to this wonderful project. When he
          remembers that he works for children all the fuss goes away.
        </p>
      </>
    ),
    image: 'img/about/2_DANIEL.png',
    realImage: 'img/about/real/2_DanielFernandez.png',
  },
  {
    name: 'Óscar',
    role: 'Web Developer',
    description: (
      <>
        <p>
          Always questioning the world around him, he blends a scientific
          mindset with creativity.
        </p>
        <p>
          With a Physics degree, he has worked as a researcher, translator,
          teacher, photographer, web designer and developer, while also
          designing experiments for a science parody TV show, shooting
          stop-motion films, and producing live funky music shows.
        </p>
        <p>
          He loves getting lost in forests, photographing the strange, and scuba
          diving.
        </p>
      </>
    ),
    image: 'img/about/3_OSCAR.png',
    realImage: 'img/about/real/3_OscarEspeso.png',
  },
  {
    name: 'Pep',
    role: 'Software Engineer',
    description: (
      <>
        <p>
          The software engineer in the shadows, keeping Rockalingua running
          smoothly.
        </p>
        <p>
          Known for a curious mind, always ready to dive into new challenges,
          and quick to fix anything that breaks.
        </p>
        <p>
          Driven by a passion for problem-solving to make sure every user has a
          seamless experience.
        </p>
        <p>
          He doesn't see the point in these little fun facts, but if you must
          know, he likes biking, music, dogs and food!
        </p>
      </>
    ),
    image: 'img/about/4_PEP.png',
    realImage: 'img/about/real/4_Pep.png',
  },
  {
    name: 'Valerie',
    role: 'Support & Teacher Success Queen',
    description: (
      <>
        <p>
          In 2011 Valerie traded the rolling hills of Pennsylvania for the
          wetter, but still rolling hills of Northern Spain. With a background
          in Cultural Anthropology and Spanish, she's been a teacher,
          translator, and content writer.
        </p>
        <p>
          As part of the Rockalingua Team she wears many hats, but most enjoys
          helping others unlock the magic of the program. She and her co-worker
          (a black and white dog named Pulmón) work hard to make sure teachers,
          students and families can all enjoy Rockalingua.
        </p>
        <p>
          When she's not at work, she enjoys long walks in the park, trail runs,
          and is always up for an adventure, especially if it's out in nature.
        </p>
      </>
    ),
    image: 'img/about/5_VALERIE.png',
    realImage: 'img/about/real/5_ValerieSpring.png',
  },
  {
    name: 'Ana',
    role: 'Craft Master',
    description: (
      <>
        <p>Meet Ana Gómez! Her name is Ana María, but just call her Ana!</p>
        <p>
          Born and raised in Barcelona, she moved to Madrid ten years ago for
          work. Though she once dreamed of being a primary school teacher, she
          found her true calling teaching Spanish to students of all ages in
          Spain and France.
        </p>
        <p>
          When she's not in the classroom, Ana is creating activities, recording
          craft tutorials, or sharing ideas on her website, SuperLápiz, to
          inspire fellow teachers. And when she's off the clock, you'll find her
          walking her dog Chicho, cooking tortilla de patatas, or passionately
          debating Eurovision with friends!
        </p>
      </>
    ),
    image: 'img/about/6_ANA.png',
    realImage: 'img/about/real/6_AnaGomez.png',
  },
  {
    name: 'Paco',
    role: 'Music Wizard',
    description: (
      <>
        <p>
          Francisco, or Paco as everyone calls him, was born and raised in
          Madrid.
        </p>
        <p>
          He works his magic in the studio, producing and recording
          Rockalingua's fun and catchy songs. He also adds voices and sound
          effects to videos and stories, making them even more engaging for
          Spanish learners.
        </p>
        <p>
          But his most important role? Being Inés' dad—though she might say his
          best talent is making everything sound cooler with the right effects!
        </p>
      </>
    ),
    image: 'img/about/7_PACO.png',
    realImage: 'img/about/real/7_PacoMusica.png',
  },
  {
    name: 'Inés',
    role: 'Singer from Heaven',
    description: (
      <>
        <p>
          Inés, also from Madrid, has a voice made for storytelling—literally!
        </p>
        <p>
          As a singer and voice actor, she narrates stories and exercises, sings
          songs in both English and Spanish, and brings many of Rockalingua's
          characters to life.
        </p>
        <p>
          Working with her dad isn't always easy (he has a lot of opinions about
          sound!), but together they make a great artistic team, creating music
          and stories that make learning Spanish fun.
        </p>
      </>
    ),
    image: 'img/about/8_INES.png',
    realImage: 'img/about/real/8_Ines_Musica.png',
  },
  {
    name: 'Rose',
    role: 'French Language Specialist',
    description: (
      <>
        <p>
          Meet Rosalind! A lifelong language learner and teacher, Rosalind
          joined the Rockalingua team in 2023 as our resident French proofreader
          and translator. From the moment she started, she fell in love with
          many of the Rockalingua characters, especially Mademoiselle Jaune!
        </p>
        <p>
          When she's not busy translating, you'll find her whizzing around on
          her bike, singing along to Rockalingua's tunes, or heading to the
          beach for a swim in the sea.
        </p>
        <p>
          Oh, and don't be surprised if you catch her with her head buried in a
          dictionary, she's a true word lover at heart!
        </p>
      </>
    ),
    image: 'img/about/9_ROSE.png',
    realImage: 'img/about/real/9_RoseRosae.png',
  },
  {
    name: 'Gloria',
    role: 'Teacher Guide Author',
    description: (
      <>
        <p>
          Gloria Arizaga is a native Spanish speaker from Mexico, transplanted
          to Illinois.
        </p>
        <p>
          Her background includes journalism, educational consulting, and over a
          decade of teaching Spanish to students of all ages. She loves making
          language learning fun and engaging by incorporating music and art.
        </p>
        <p>
          Outside the classroom, Gloria enjoys volunteering, reading, gardening,
          cooking, and spending time outdoors. She's also a fan of history and
          science documentaries and enjoys experimenting with chemistry in her
          kitchen and garden.
        </p>
      </>
    ),
    image: 'img/about/10_GLORIA.png',
    realImage: 'img/about/real/11_GloriaGarizaga.png',
  },
  {
    name: 'Diego',
    role: 'Teacher trainer Guru',
    description: (
      <>
        <p>
          Diego is a passionate Spanish teacher, poet, blogger, and world
          language educator.
        </p>
        <p>
          Originally from Bogotá, Colombia, he has taught in both his home
          country and the U.S., bringing cultural awareness and fresh
          perspectives to Spanish instruction. He loves helping students connect
          with the language in meaningful ways.
        </p>
        <p>
          Diego is also the co-founder of #LangChat and #CharlaELE1, two online
          spaces where Spanish teachers collaborate and grow. When he's not in
          the classroom, you'll find him writing poetry, sharing ideas with
          fellow educators, or exploring new ways to make language learning
          engaging
        </p>
      </>
    ),
    image: 'img/about/12_DIEGO.png',
    realImage: 'img/about/real/12_DiegoOjeda.png',
  },
  {
    name: 'Corinne',
    role: 'Multilingual Education Expert',
    description: (
      <>
        <p>
          Corinne was born in Senegal and grew up in a multilingual household,
          sparking a lifelong passion for languages.
        </p>
        <p>
          She speaks eight languages, including French, Spanish, English, and
          Wolof. For nearly 30 years, she has lived in the UK, where she teaches
          French and Spanish in primary schools.
        </p>
        <p>
          In her spare time, she enjoys translating, swimming, traveling, and
          discovering new cuisines. Corinne also has a strong interest in
          current affairs, environmental issues, and the impact of climate
          change on developing countries.
        </p>
      </>
    ),
    image: 'img/about/13_CORINNE.png',
    realImage: 'img/about/real/13_Corinne.png',
  },
  {
    name: 'Carmen Reyes',
    role: 'Spanish Teacher & Teacher Trainer',
    description: (
      <>
        <p>
          Meet Carmen! Originally from Lima, Peru, she brings her love for
          language, culture, and creativity into every Spanish lesson. Whether
          she's leading engaging activities, sharing vibrant traditions, or
          singing along to Rockalingua songs with her students, her energy is
          contagious!
        </p>
        <p>
          Carmen is also an author and passionate about supporting fellow
          teachers, sharing ideas and strategies to make Spanish teaching both
          effective and engaging.
        </p>
        <p>
          And when she's not in teaching mode, you'll probably find her enjoying
          a good book, a strong cup of coffee, and a pastry (or some dark
          chocolate—because balance!).Teaching, culture, and coffee—her ultimate
          trio!
        </p>
      </>
    ),
    image: 'img/about/14_CARMEN.webp',
    realImage: 'img/about/real/14_Carmen.webp',
  },
];
